import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadDialog from "@/components/LeadDialog";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Building, Wallet, Users, ShieldCheck, Lightbulb, Droplet,
  CircleCheck, TreePine, Sparkles,
} from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const HERO_IMG = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=2000&q=85&auto=format&fit=crop";

export default function HousingSocieties() {
  const { t } = useTranslation();
  const [leadOpen, setLeadOpen] = useState(false);
  const openQuote = () => setLeadOpen(true);

  const BENEFITS = [
    { icon: Wallet, slug: "b1", title: t("soc.b1_t"), desc: t("soc.b1_d") },
    { icon: ShieldCheck, slug: "b2", title: t("soc.b2_t"), desc: t("soc.b2_d") },
    { icon: Users, slug: "b3", title: t("soc.b3_t"), desc: t("soc.b3_d") },
    { icon: Lightbulb, slug: "b4", title: t("soc.b4_t"), desc: t("soc.b4_d") },
    { icon: Droplet, slug: "b5", title: t("soc.b5_t"), desc: t("soc.b5_d") },
    { icon: TreePine, slug: "b6", title: t("soc.b6_t"), desc: t("soc.b6_d") },
  ];

  const STATS = [
    { k: "₹78,000", v: t("soc.stat_subsidy") },
    { k: "80–95%", v: t("soc.stat_reduction") },
    { k: "4–5 yrs", v: t("soc.stat_payback") },
    { k: "25 yrs", v: t("soc.stat_warranty") },
  ];

  const STEPS = [
    { n: "01", t: t("soc.st1_t"), d: t("soc.st1_d") },
    { n: "02", t: t("soc.st2_t"), d: t("soc.st2_d") },
    { n: "03", t: t("soc.st3_t"), d: t("soc.st3_d") },
    { n: "04", t: t("soc.st4_t"), d: t("soc.st4_d") },
  ];

  const OFFERINGS = [
    t("soc.o1"), t("soc.o2"), t("soc.o3"), t("soc.o4"), t("soc.o5"), t("soc.o6"),
  ];

  const FAQS = [
    { q: t("soc.fq1_q"), a: t("soc.fq1_a") },
    { q: t("soc.fq2_q"), a: t("soc.fq2_a") },
    { q: t("soc.fq3_q"), a: t("soc.fq3_a") },
    { q: t("soc.fq4_q"), a: t("soc.fq4_a") },
  ];

  return (
    <div className="relative bg-white">
      <Navbar onCtaClick={openQuote} />

      <section data-testid="society-hero" className="relative pt-[88px] min-h-[80vh] flex items-end overflow-hidden bg-stone-950">
        <img src={HERO_IMG} alt="Housing society" className="absolute inset-0 w-full h-full object-cover opacity-65" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 text-white">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1.5 text-[11px] font-bold tracking-[0.18em] uppercase">
            <Building className="w-3.5 h-3.5" /> {t("soc.hero_badge")}
          </div>
          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
            {t("soc.hero_title_a")}<br />
            <span className="text-orange-400">{t("soc.hero_title_hl")}</span>
          </h1>
          <p className="mt-5 text-white/85 max-w-2xl text-lg leading-relaxed">{t("soc.hero_subtitle")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={openQuote} data-testid="society-cta-quote" className="btn-premium rounded-full bg-orange-500 hover:bg-orange-600 text-white h-12 px-7 font-semibold">
              {t("soc.cta_quote")}<ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
            <Link to="/#calculator" data-testid="society-cta-calc" className="inline-flex items-center justify-center rounded-full h-12 px-7 border border-white/30 bg-white/5 text-white font-semibold hover:bg-white/10 transition" reloadDocument>
              {t("soc.cta_calc")}
            </Link>
          </div>
        </div>
      </section>

      <section className="border-y border-stone-200 bg-stone-50">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={i} data-testid={`society-stat-${i}`}>
              <div className="font-display font-extrabold text-3xl md:text-4xl text-green-700 leading-none">{s.k}</div>
              <div className="mt-2 text-stone-600 text-sm leading-snug">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("soc.b_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("soc.b_title_a")} <span className="text-green-700">{t("soc.b_title_hl")}</span>
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.slug} data-testid={`society-benefit-${b.slug}`} className="premium-card group p-7 rounded-3xl border border-stone-200 bg-white hover:border-green-300 hover:premium-shadow transition">
                  <div className="w-12 h-12 rounded-2xl bg-green-100 grid place-items-center group-hover:bg-green-600 transition">
                    <Icon className="w-6 h-6 text-green-700 group-hover:text-white transition" strokeWidth={2.2} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-xl text-stone-900">{b.title}</h3>
                  <p className="mt-3 text-stone-600 leading-relaxed text-[15px]">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-stone-50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("soc.st_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("soc.st_title_a")} <span className="text-green-700">{t("soc.st_title_hl")}</span> {t("soc.st_title_b")}
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((p) => (
              <div key={p.n} data-testid={`society-step-${p.n}`} className="p-6 rounded-2xl bg-white border border-stone-200">
                <div className="font-handwritten text-5xl text-green-700 leading-none">{p.n}</div>
                <h3 className="mt-3 font-display font-bold text-lg text-stone-900">{p.t}</h3>
                <p className="mt-2 text-stone-600 leading-relaxed text-sm">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="aspect-[4/5] rounded-[28px] overflow-hidden shadow-2xl border border-stone-200">
              <img src="https://images.unsplash.com/photo-1773814798607-b6b4013cc757?w=1400&q=85&auto=format&fit=crop" alt="Apartment building" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-orange-500 text-white rounded-2xl p-5 shadow-xl max-w-[260px]">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-90">
                <Sparkles className="w-4 h-4" /> {t("soc.o_badge")}
              </div>
              <div className="mt-1 font-display font-black text-xl">{t("soc.o_badge_title")}</div>
              <div className="text-xs opacity-90">{t("soc.o_badge_caption")}</div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("soc.o_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("soc.o_title")}
            </h2>
            <ul className="mt-8 space-y-3">
              {OFFERINGS.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-stone-700 text-[15px] leading-relaxed">
                  <CircleCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2.4} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <Button onClick={openQuote} data-testid="society-cta-stack" className="btn-premium mt-9 rounded-full bg-green-600 hover:bg-green-700 text-white h-12 px-7 font-semibold">
              {t("soc.o_cta")}<ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-stone-50 border-y border-stone-200">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("soc.fq_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("soc.fq_title_a")} <span className="text-green-700">{t("soc.fq_title_hl")}</span>
            </h2>
          </div>
          <div className="mt-12 space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} data-testid={`society-faq-${i}`} className="group p-6 rounded-2xl bg-white border border-stone-200 open:border-green-300 open:shadow-md transition">
                <summary className="cursor-pointer list-none font-display font-bold text-lg text-stone-900 flex items-start justify-between gap-4">
                  {f.q}
                  <span className="text-green-600 text-2xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-stone-600 leading-relaxed text-[15px]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-5xl text-stone-900 leading-tight">
            {t("soc.final_a")} <span className="text-green-700">{t("soc.final_hl")}</span>
          </h2>
          <p className="mt-4 text-stone-600 text-lg max-w-2xl mx-auto">{t("soc.final_sub")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button onClick={openQuote} data-testid="society-cta-final" className="btn-premium rounded-full bg-orange-500 hover:bg-orange-600 text-white h-12 px-7 font-semibold">
              {t("soc.final_cta")}<ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
            <Link to="/" className="inline-flex items-center justify-center rounded-full h-12 px-7 border border-stone-300 bg-white text-stone-800 font-semibold hover:bg-stone-100 transition">
              {t("soc.back_home")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <LeadDialog
        open={leadOpen}
        onOpenChange={setLeadOpen}
        context={{ segment: "society" }}
        title={t("soc.lead_title")}
        subtitle={t("soc.lead_subtitle")}
        source="society_page"
      />
    </div>
  );
}
